import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Upload, Download, Trash2, Save } from "lucide-react";
import { useApi, useFetch, errMsg, inr, lastMonth, calcPreview } from "../lib/api";
import { PageHeader, Empty, Pill, VendorMonthBar, useVendors } from "../components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { PayrollRun } from "../components/PayrollRun";
import { PtReturn } from "../components/PtReturn";

const ManualEntry = ({ vendorId, month, onSaved }) => {
  const api = useApi();
  const { data: workers } = useFetch(`/workers?vendor_id=${vendorId}`);
  const [f, setF] = useState({ days_worked: 26 });
  const p = calcPreview(f.days_worked, f.gross_wages_earned, f.basic_wages_for_pf);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/wages", { vendor_id: vendorId, billing_month_year: month, worker_id: f.worker_id,
        days_worked: Number(f.days_worked), gross_wages_earned: Number(f.gross_wages_earned), basic_wages_for_pf: Number(f.basic_wages_for_pf) });
      toast.success("Wage record saved"); onSaved();
    } catch (er) { toast.error(errMsg(er)); }
  };
  const num = (k, label) => (
    <label className="grid gap-1.5"><span className="text-xs text-slate-400">{label}</span>
      <input type="number" min={k === "days_worked" ? 1 : 0} max={k === "days_worked" ? 31 : undefined}
        step={k === "days_worked" ? 1 : 0.01} required className="c-input font-mono" value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`wage-${k}-input`} /></label>
  );

  return (
    <form onSubmit={save} className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="grid gap-1.5 sm:col-span-2"><span className="text-xs text-slate-400">Worker</span>
          <select className="c-input" required value={f.worker_id || ""} onChange={(e) => set("worker_id", e.target.value)} data-testid="wage-worker-select">
            <option value="">Select worker…</option>{(workers || []).map((w) => <option key={w.id} value={w.id}>{w.worker_full_name} · {w.uan_number || "no UAN"}</option>)}
          </select></label>
        {num("days_worked", "Days worked (max 31)")}
        {num("gross_wages_earned", "Gross wages earned (₹)")}
        {num("basic_wages_for_pf", "Basic wages for PF (₹)")}
        <div className="flex items-end"><Button type="submit" data-testid="wage-manual-save-btn" className="w-full bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold"><Save size={15} className="mr-2" />Save record</Button></div>
      </div>
      <div className="rounded-xl border border-[#1F2A3E] bg-[#0B1017] p-5 font-mono text-sm grid gap-2" data-testid="wage-calc-preview">
        <div className="c-eyebrow mb-2">Live statutory calc</div>
        <div className="flex justify-between"><span className="text-slate-500">EPF employee 12%</span><span>{inr(p.pf)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">EPF employer 12%</span><span>{inr(p.pf)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">ESIC employee 0.75%</span><span>{inr(p.ee)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">ESIC employer 3.25%</span><span>{inr(p.er)}</span></div>
        <div className="flex justify-between border-t border-[#1F2A3E] pt-2"><span className="text-slate-400">Net payout</span><span className="text-emerald-400 font-semibold">{inr(p.net)}</span></div>
        <div className={`text-xs mt-2 ${p.breach ? "text-rose-400" : "text-slate-500"}`}>Daily rate {inr(p.daily)} {p.breach ? "· BELOW ₹450 DEFAULT FLOOR" : "· default floor ₹450/day; Wage Master rate applies on save"}</div>
      </div>
    </form>
  );
};

const CsvUpload = ({ vendorId, month, onSaved }) => {
  const api = useApi();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const template = () => {
    const csv = "uan_number,worker_full_name,days_worked,gross_wages_earned,basic_wages_for_pf\n100000007919,Ramesh Patil,26,18200,11000\n";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "comply369_wage_template.csv"; a.click();
  };
  const upload = async () => {
    const fd = new FormData();
    fd.append("vendor_id", vendorId); fd.append("billing_month", month); fd.append("file", file);
    setBusy(true);
    try { const r = (await api.post("/wages/csv", fd)).data; setResult(r); toast.success(`${r.processed} rows processed`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="grid gap-4">
      <p className="text-sm text-slate-400">Columns: <span className="font-mono text-xs text-slate-300">uan_number, worker_full_name, days_worked, gross_wages_earned, basic_wages_for_pf</span>. Workers are matched by UAN, then by name. Existing rows for the month are updated.</p>
      <div className="flex flex-wrap gap-3 items-center">
        <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} data-testid="wage-csv-upload-input"
          className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#1A2234] file:px-3 file:py-2 file:text-slate-200" />
        <Button onClick={upload} disabled={!file || busy} data-testid="wage-csv-submit-btn" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold"><Upload size={15} className="mr-2" />{busy ? "Processing…" : "Upload CSV"}</Button>
        <Button variant="outline" onClick={template} data-testid="wage-csv-template-btn" className="border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white"><Download size={15} className="mr-2" />Template</Button>
      </div>
      {result && (
        <div className="rounded-lg border border-[#1F2A3E] bg-[#0B1017] p-4 text-sm font-mono" data-testid="wage-csv-result">
          {result.processed}/{result.total_rows} rows processed · {result.breaches} breach(es)
          {result.errors.map((e) => <div key={e} className="text-rose-400 text-xs mt-1">{e}</div>)}
        </div>
      )}
    </div>
  );
};

export default function Wages() {
  const api = useApi();
  const { data: vendors } = useVendors();
  const [vendorId, setVendorId] = useState("");
  const [pt, setPt] = useState(false);
  const [month, setMonth] = useState(lastMonth());
  const { data, reload } = useFetch(vendorId && month ? `/wages?vendor_id=${vendorId}&month=${month}` : null);
  useEffect(() => { if (!vendorId && vendors?.length) setVendorId(vendors[0].id); }, [vendors, vendorId]);

  const remove = async (r) => {
    try { await api.delete(`/wages/${r.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Payroll ledger" title="Payroll" desc="Rules follow each site's state: EPF 12% on wages (basic + DA, at least half of gross under the Labour Codes) up to ₹25,000 from 17 Sep 2026; ESIC 0.75% / 3.25% when gross is ₹21,000 or less; PT and LWF by state; breach when pay falls below the higher of your Wage Master and the state minimum wage." />
      <VendorMonthBar vendors={vendors} vendorId={vendorId} setVendorId={setVendorId} month={month} setMonth={setMonth} />
      <div className="flex justify-end -mt-2 mb-4"><Button variant="outline" onClick={() => setPt(true)} className="border-[#1F2A3E] h-9 text-xs" data-testid="pt-return-open-btn">Professional Tax return</Button></div>
      {pt && <PtReturn month={month} onClose={() => setPt(false)} />}
      {vendorId ? (
        <>
          <div className="c-surface p-6 mb-6 c-rise">
            <Tabs defaultValue="payroll" data-testid="wage-entry-tabs">
              <TabsList className="bg-[#0B1017] mb-6">
                <TabsTrigger value="payroll" data-testid="wage-tab-payroll">From attendance</TabsTrigger>
                <TabsTrigger value="manual" data-testid="wage-tab-manual">Manual entry</TabsTrigger>
                <TabsTrigger value="csv" data-testid="wage-tab-csv">CSV upload</TabsTrigger>
              </TabsList>
              <TabsContent value="payroll"><PayrollRun vendorId={vendorId} month={month} onSaved={reload} /></TabsContent>
              <TabsContent value="manual"><ManualEntry key={vendorId} vendorId={vendorId} month={month} onSaved={reload} /></TabsContent>
              <TabsContent value="csv"><CsvUpload vendorId={vendorId} month={month} onSaved={reload} /></TabsContent>
            </Tabs>
          </div>
          <div className="c-surface overflow-x-auto c-rise">
            <table className="c-table w-full">
              <thead><tr><th>Worker</th><th>Days</th><th className="!text-right">Gross</th><th className="!text-right">Basic</th><th className="!text-right">PF EE/ER</th><th className="!text-right">ESIC EE/ER</th><th className="!text-right">PT / LWF</th><th className="!text-right">Net</th><th>Status</th><th /></tr></thead>
              <tbody>
                {(data || []).map((r) => (
                  <tr key={r.id} data-testid="wage-table-row" className={r.min_wage_breach ? "bg-rose-500/5" : ""}>
                    <td className="font-medium">{r.worker_full_name}<div className="text-[10px] text-slate-500 font-mono">{r.uan_number}</div></td>
                    <td className="font-mono">{r.days_worked}</td>
                    <td className="font-mono text-right">{inr(r.gross_wages_earned)}</td>
                    <td className="font-mono text-right">{inr(r.basic_wages_for_pf)}</td>
                    <td className="font-mono text-right text-xs">{inr(r.calculated_pf_employee)} / {inr(r.calculated_pf_employer)}</td>
                    <td className="font-mono text-right text-xs">{inr(r.calculated_esic_employee)} / {inr(r.calculated_esic_employer)}</td>
                    <td className="font-mono text-right text-xs" data-testid="wage-pt-lwf">{inr(r.calculated_pt)} / {inr(r.calculated_lwf_employee)}{r.rules_state && <div className="text-[10px] text-slate-500">{r.rules_state}</div>}</td>
                    <td className="font-mono text-right text-emerald-400">{inr(r.net_payout)}</td>
                    <td>{r.min_wage_breach ? <Pill tone="red" testid="wage-breach-badge">Min wage breach</Pill> : <Pill tone="green">OK</Pill>}</td>
                    <td><button className="p-1.5 text-slate-400 hover:text-rose-400" onClick={() => remove(r)} data-testid="wage-delete-btn"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.length === 0 && <Empty text="No wage records for this vendor and month." />}
          </div>
        </>
      ) : <div className="c-surface"><Empty text="Select a vendor to enter wages." /></div>}
    </div>
  );
}
